import { IDetailsRowProps } from '@fluentui/react';
import {
  DetailsList,
  EmptyMessage,
  IColumnWithDataType,
  IDetailsListProps,
  listCellStylesNoLeftRightPadding,
  ListRow,
  listRowStylesWidgetLine,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
} from '@h2oai/ui-kit';
import { useHistory } from 'react-router-dom';

import { PermissionState } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle } from '../../featurestore/models';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import type { AccessRequest } from '../AccessRequestList/AccessRequestList';
import {
  type IPendingPermissionListClassNames,
  type IPendingPermissionListStyles,
  pendingPermissionListStylesDefault,
} from './PendingPermissionList.styles';

const columns: IColumnWithDataType[] = [
  {
    key: 'name',
    name: 'Name',
    fieldName: 'description',
    minWidth: 100,
    data: {
      headerFieldName: 'name',
      listCellProps: {
        emptyMessage: 'No Description',
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
  {
    key: 'createdOn',
    name: 'Created On',
    fieldName: 'createdOn',
    minWidth: 180,
    data: {
      listCellProps: {
        onRenderText: (item: AccessRequest) => <>{formatDefaultDateAndTimeWithSeconds(item.createdOn)}</>,
      },
    },
  },
  {
    key: 'lastUpdateOn',
    name: 'Updated On',
    fieldName: 'lastUpdateOn',
    minWidth: 180,
    data: {
      listCellProps: {
        onRenderText: (item: AccessRequest) => <>{formatDefaultDateAndTimeWithSeconds(item.lastUpdateOn)}</>,
      },
    },
  },
  {
    key: 'AccessType',
    name: 'Access Type',
    fieldName: 'permission',
    minWidth: 160,
  },
];

interface IPendingPermissionListProps {
  title: ContentsTitle;
  data: AccessRequest[];
  loading: boolean;
}

export const PendingPermissionList = ({ title, data, loading }: IPendingPermissionListProps) => {
  const history = useHistory();
  const detailsListProps: IDetailsListProps = {
    columns,
    isHeaderVisible: false,
    items: data,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return (
        <ListRow
          styles={listRowStylesWidgetLine}
          data={item}
          columns={columns}
          loading={item.isNotLoaded}
          onClick={(e) => {
            history.push(
              `${
                title === ContentsTitle.projects
                  ? RoutePaths.FEATURESTORE_PROJECT_PERMISSIONS
                  : RoutePaths.FEATURESTORE_FEATURE_SETS_PERMISSIONS
              }?tab=${ContentsTitle.myPermissions}&filter=${PermissionState.PENDING}`
            );
            e?.preventDefault();
          }}
        />
      );
    },
  };
  const classNames = useClassNames<IPendingPermissionListStyles, IPendingPermissionListClassNames>(
    'PendingPermissionList',
    pendingPermissionListStylesDefault
  );
  return (
    <div className={classNames.root} data-test={`${title.toLowerCase().replace(/\s/g, '')}-pending-permission-list`}>
      {loading ? (
        <Loader styles={loaderStylesSpinnerXLarge} label="Loading" />
      ) : data.length ? (
        <div className={classNames.list} data-is-scrollable="true">
          <DetailsList {...detailsListProps} />
        </div>
      ) : (
        <EmptyMessage message="You don't have any pending permissions" />
      )}
    </div>
  );
};
